import { auth } from "@/firebase/init";
import { db } from "@/firebase/init";
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  deleteUser, 
  updateEmail, 
  updatePassword, 
  EmailAuthProvider, 
  reauthenticateWithCredential, 
  sendPasswordResetEmail,
  sendEmailVerification
} from "firebase/auth";
import { doc, deleteDoc, setDoc } from "firebase/firestore";


/**
 * Register a new user
 */
export const registerUser = async (email, password, role = "user") => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const userId = userCredential.user.uid;
    
await setDoc(doc(db, "users", userId), {
    email,
    role, 
    registrationDate: new Date(),
    registrationDateString: new Date().toString(),
    registrationDateShort: new Date().toISOString().split("T")[0],
    registrationMonth: new Date().toLocaleString("default", { month: "long" }),
    registrationDay: new Date().toLocaleDateString("en-US", { weekday: "long" }),
    name: "",
    surname: "",
    university: {
      name: "",
      studentNumber: "",
      faculty: "",
      department: "",
      levelOfStudy: "", // e.g. "Undergraduate", "Postgraduate"
    },
    profilePicture: "",
    
  });


    return userCredential.user;
  } catch (error) {
    console.error("Error registering user:", error);
    throw error;
  }
};

/**
 * Log in a user
 */
export const loginUser = async (email, password) => {
  try {
    return await signInWithEmailAndPassword(auth, email, password);
  } catch (error) {
    console.error("Login error:", error);
    throw error;
  }
};

/**
 * Log out a user
 */
export const logoutUser = async () => {
  try {
    return await signOut(auth);
  } catch (error) {
    console.error("Logout error:", error);
    throw error;
  }
};

/**
 * Re-authenticate user before sensitive actions
 */
export const reauthenticateUser = async (password) => {
  try {
    if (!auth.currentUser) throw new Error("No user is currently logged in.");
    
    const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
    await reauthenticateWithCredential(auth.currentUser, credential);
  } catch (error) {
    console.error("Reauthentication error:", error);
    throw error;
  }
};

/**
 * Update user email (requires re-authentication)
 */
export const updateUserEmail = async (newEmail, password) => {
  try {
    await reauthenticateUser(password);
    await updateEmail(auth.currentUser, newEmail);
  } catch (error) {
    console.error("Error updating email:", error);
    throw error;
  }
};

/**
 * Update user password (requires re-authentication)
 */
export const updateUserPassword = async (newPassword, password) => {
  try {
    await reauthenticateUser(password);
    await updatePassword(auth.currentUser, newPassword);
  } catch (error) {
    console.error("Error updating password:", error);
    throw error;
  }
};

/**
 * Send password reset email
 */
export const sendPasswordReset = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error("Error sending password reset email:", error);
    throw error;
  }
};

/**
 * Delete user account (requires re-authentication)
 */
export const deleteAccount = async (password) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No user is currently logged in.");

    await reauthenticateUser(password);
    
    // Delete user document from Firestore
    await deleteDoc(doc(db, "users", user.uid));
    
    // Delete authentication account
    await deleteUser(user);
  } catch (error) {
    console.error("Error deleting account:", error);
    throw error;
  }
};

/**
 * Send email verification
 */
export const sendVerificationEmail = async () => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No user is currently logged in.");
    
    await sendEmailVerification(user);
  } catch (error) {
    console.error("Error sending verification email:", error);
    throw error;
  }
};

/**
 * Check if email is verified (reloads the user to get fresh status)
 */
export const checkEmailVerified = async () => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No user is currently logged in.");
    
    // Reload user to get the most up-to-date information
    await user.reload();
    return user.emailVerified;
  } catch (error) {
    console.error("Error checking email verification status:", error);
    throw error;
  }
};
